// src/CalendarPage.js - Complete file with fixed "All Day" handling, ESLint fixes, direct add for searched events to avoid async state issues, event list filtered by current calendar view (strict month bounds without week overlap for month view), added sorting to displayedEvents, and error handling for search timeout

import React, { useState, useEffect } from "react";
import { db, auth } from "./firebase.js";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where } from "firebase/firestore";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { getFunctions, httpsCallable } from "firebase/functions";

const localizer = momentLocalizer(moment);
const functions = getFunctions();

function CalendarPage({ user, isAdmin, isMobile }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [recurrenceType, setRecurrenceType] = useState("none"); // "none", "weekly", "monthly", "yearly"
  const [occurrences, setOccurrences] = useState("1"); // Number of occurrences
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editScope, setEditScope] = useState("this"); // "this" or "all" for editing/deleting recurring events

  // New states for local events search
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [timeframe, setTimeframe] = useState("1 month");
  const [searchedEvents, setSearchedEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [currentIncompleteEvent, setCurrentIncompleteEvent] = useState(null);
  const [detailTitle, setDetailTitle] = useState("");
  const [detailDate, setDetailDate] = useState("");
  const [detailTime, setDetailTime] = useState("");

  // States for calendar view filtering
  const [view, setView] = useState('month'); // Default view
  const [currentDate, setCurrentDate] = useState(new Date()); // Current date in view
  const [displayedEvents, setDisplayedEvents] = useState([]); // Filtered events for list

  // State for event popup
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Fetch events from Firestore on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        console.log("Fetching events from Firestore...");
        const snapshot = await getDocs(collection(db, "events"));
        console.log("Events snapshot size:", snapshot.size);
        const eventList = snapshot.docs.map(doc => {
          const data = doc.data();
          let start, end, dateStr, timeStr;

          // Support both rich automated events (start Timestamp) and manual events (date + time strings)
          if (data.start && typeof data.start.toDate === 'function') {
            const startDate = data.start.toDate();
            const m = moment(startDate);
            dateStr = m.format('YYYY-MM-DD');
            timeStr = (m.hours() === 0 && m.minutes() === 0) ? '' : m.format('HH:mm');
            start = startDate;
            end = data.end && typeof data.end.toDate === 'function' ? data.end.toDate() : startDate;
          } else if (data.time && moment(`${data.date}T${data.time}:00`, "YYYY-MM-DDTHH:mm:ss", true).isValid()) {
            const dateTimeString = `${data.date}T${data.time}:00`;
            start = moment(dateTimeString).toDate();
            end = moment(dateTimeString).toDate();
            dateStr = data.date;
            timeStr = data.time;
          } else if (moment(data.date, "YYYY-MM-DD", true).isValid()) {
            start = moment(data.date).startOf('day').toDate();
            end = moment(data.date).endOf('day').toDate();
            dateStr = data.date;
            timeStr = data.time || '';
          } else {
            return null;
          }

          return {
            id: doc.id,
            title: data.title,
            date: dateStr,
            time: timeStr || "",
            recurrenceId: data.recurrenceId || null,
            recurrenceType: data.recurrenceType || "none",
            start: start,
            end: end,
            allDay: !timeStr,
            // Rich fields from automated sync
            location: data.location || "",
            description: data.description || "",
            url: data.url || "",
            category: data.category || "",
            source: data.source || ""
          };
        }).filter(event => event !== null);
        console.log("Events list:", eventList);
        setEvents(eventList);
      } catch (error) {
        console.error("Error fetching events:", error);
        setError("Failed to fetch events: " + error.message);
      }
    };
    fetchEvents();
  }, []);

  // Filter events for the list based on current view and date, and sort them
  useEffect(() => {
    let startDate, endDate;
    const currentMoment = moment(currentDate);

    switch (view) {
      case 'month':
        // Strict month bounds without overlapping weeks
        startDate = currentMoment.clone().startOf('month');
        endDate = currentMoment.clone().endOf('month');
        break;
      case 'week':
        startDate = currentMoment.clone().startOf('week');
        endDate = currentMoment.clone().endOf('week');
        break;
      case 'day':
        startDate = currentMoment.clone().startOf('day');
        endDate = currentMoment.clone().endOf('day');
        break;
      case 'agenda':
        // For agenda, show next 30 days as default
        startDate = currentMoment.clone().startOf('day');
        endDate = currentMoment.clone().add(30, 'days').endOf('day');
        break;
      default:
        startDate = currentMoment.clone().startOf('month');
        endDate = currentMoment.clone().endOf('month');
    }

    const filteredEvents = events.filter(event => {
      const eventStart = moment(event.start);
      return eventStart.isBetween(startDate, endDate, null, '[]');
    }).sort((a, b) => moment(a.start) - moment(b.start)); // Sort by start date ascending

    setDisplayedEvents(filteredEvents);
    console.log(`Filtered displayedEvents for view ${view} from ${startDate.format('YYYY-MM-DD')} to ${endDate.format('YYYY-MM-DD')}:`, filteredEvents.length);
  }, [view, currentDate, events]);

  console.log("CalendarPage rendering with isAdmin:", isAdmin);

  const addEventDirect = async (eventTitle, eventDate, eventTime, eventRecurrenceType, eventOccurrences) => {
    setError(null);
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log("Forcing token refresh before write...");
        const token = await currentUser.getIdToken(true);
        const idTokenResult = await currentUser.getIdTokenResult();
        console.log("Refreshed token:", token);
        console.log("Token claims during write:", idTokenResult.claims);
        console.log("isAdmin from props:", isAdmin);
      } else {
        throw new Error("No authenticated user");
      }

      if (!moment(eventDate, "YYYY-MM-DD", true).isValid()) {
        throw new Error("Invalid date format. Use YYYY-MM-DD.");
      }

      const numOccurrences = parseInt(eventOccurrences, 10);
      if (isNaN(numOccurrences) || numOccurrences < 1) {
        throw new Error("Number of occurrences must be at least 1");
      }

      const baseEvent = {
        title: eventTitle,
        date: eventDate,
        time: eventTime || null,
        createdBy: currentUser.uid,
        createdAt: new Date(),
        recurrenceType: eventRecurrenceType,
      };

      const newEvents = [];

      if (eventRecurrenceType !== "none") {
        // Generate recurring events based on type and occurrences
        const recurrenceId = `${eventTitle}_${Date.now()}`;
        const startDate = moment(eventDate);
        for (let i = 0; i < numOccurrences; i++) {
          let eventDateRecur;
          if (eventRecurrenceType === "weekly") {
            eventDateRecur = startDate.clone().add(i * 7, 'days').format('YYYY-MM-DD');
          } else if (eventRecurrenceType === "monthly") {
            eventDateRecur = startDate.clone().add(i, 'months').format('YYYY-MM-DD');
          } else if (eventRecurrenceType === "yearly") {
            eventDateRecur = startDate.clone().add(i, 'years').format('YYYY-MM-DD');
          }
          const docRef = await addDoc(collection(db, "events"), {
            ...baseEvent,
            date: eventDateRecur,
            recurrenceId: recurrenceId,
          });
          console.log(`Added event on ${eventDateRecur} with ID: ${docRef.id}`);
          let start, end;
          if (eventTime && moment(`${eventDateRecur}T${eventTime}:00`, "YYYY-MM-DDTHH:mm:ss", true).isValid()) {
            const dateTimeString = `${eventDateRecur}T${eventTime}:00`;
            start = moment(dateTimeString).toDate();
            end = moment(dateTimeString).toDate();
          } else {
            start = moment(eventDateRecur).startOf('day').toDate();
            end = moment(eventDateRecur).endOf('day').toDate();
          }
          newEvents.push({
            id: docRef.id,
            title: eventTitle,
            date: eventDateRecur,
            time: eventTime || "",
            recurrenceId: recurrenceId,
            recurrenceType: eventRecurrenceType,
            start: start,
            end: end,
            allDay: !eventTime
          });
        }
        console.log(`Recurring ${eventRecurrenceType} events added successfully`);
      } else {
        // Add a single event
        const docRef = await addDoc(collection(db, "events"), {
          ...baseEvent,
          recurrenceId: null,
        });
        console.log(`Added single event on ${eventDate} with ID: ${docRef.id}`);
        let start, end;
        if (eventTime && moment(`${eventDate}T${eventTime}:00`, "YYYY-MM-DDTHH:mm:ss", true).isValid()) {
          const dateTimeString = `${eventDate}T${eventTime}:00`;
          start = moment(dateTimeString).toDate();
          end = moment(dateTimeString).toDate();
        } else {
          start = moment(eventDate).startOf('day').toDate();
          end = moment(eventDate).endOf('day').toDate();
        }
        newEvents.push({
          id: docRef.id,
          title: eventTitle,
          date: eventDate,
          time: eventTime || "",
          recurrenceId: null,
          recurrenceType: "none",
          start: start,
          end: end,
          allDay: !eventTime
        });
      }

      // Update the events state optimistically with the new events
      setEvents(prevEvents => [...prevEvents, ...newEvents]);

      // Fetch all events to ensure consistency
      const snapshot = await getDocs(collection(db, "events"));
      console.log("Fetched events after add:", snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      const eventList = snapshot.docs.map(doc => {
        const data = doc.data();
        let start, end, dateStr, timeStr;

        if (data.start && typeof data.start.toDate === 'function') {
          const startDate = data.start.toDate();
          const m = moment(startDate);
          dateStr = m.format('YYYY-MM-DD');
          timeStr = (m.hours() === 0 && m.minutes() === 0) ? '' : m.format('HH:mm');
          start = startDate;
          end = data.end && typeof data.end.toDate === 'function' ? data.end.toDate() : startDate;
        } else if (data.time && moment(`${data.date}T${data.time}:00`, "YYYY-MM-DDTHH:mm:ss", true).isValid()) {
          const dateTimeString = `${data.date}T${data.time}:00`;
          start = moment(dateTimeString).toDate();
          end = moment(dateTimeString).toDate();
          dateStr = data.date;
          timeStr = data.time;
        } else if (moment(data.date, "YYYY-MM-DD", true).isValid()) {
          start = moment(data.date).startOf('day').toDate();
          end = moment(data.date).endOf('day').toDate();
          dateStr = data.date;
          timeStr = data.time || '';
        } else {
          return null;
        }

        return {
          id: doc.id,
          title: data.title,
          date: dateStr,
          time: timeStr || "",
          recurrenceId: data.recurrenceId || null,
          recurrenceType: data.recurrenceType || "none",
          start: start,
          end: end,
          allDay: !timeStr,
          location: data.location || "",
          description: data.description || "",
          url: data.url || "",
          category: data.category || "",
          source: data.source || ""
        };
      }).filter(event => event !== null);
      setEvents(eventList);
      return true; // Success
    } catch (error) {
      console.error("Error adding event:", error);
      setError("Failed to add event: " + error.message);
      return false; // Return false to indicate failure
    }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    await addEventDirect(title, date, time, recurrenceType, occurrences);
    setTitle("");
    setDate("");
    setTime("");
    setRecurrenceType("none");
    setOccurrences("1");
  };

  const editEventData = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log("Forcing token refresh before update...");
        const token = await currentUser.getIdToken(true);
        const idTokenResult = await currentUser.getIdTokenResult();
        console.log("Refreshed token:", token);
        console.log("Token claims during update:", idTokenResult.claims);
        console.log("isAdmin from props:", isAdmin);
      } else {
        throw new Error("No authenticated user");
      }

      if (!moment(editEvent.date, "YYYY-MM-DD", true).isValid()) {
        throw new Error("Invalid date format. Use YYYY-MM-DD.");
      }

      if (editEvent.recurrenceId && editScope === "all") {
        // Update all events in the series
        const q = query(collection(db, "events"), where("recurrenceId", "==", editEvent.recurrenceId));
        const snapshot = await getDocs(q);
        for (const eventDoc of snapshot.docs) {
          const eventData = eventDoc.data();
          const eventDate = moment(eventData.date);
          const editDate = moment(editEvent.date);
          let newDate;
          if (editEvent.recurrenceType === "weekly") {
            const weekDiff = editDate.diff(eventDate, 'weeks');
            newDate = moment(eventData.date).add(weekDiff * 7, 'days').format('YYYY-MM-DD');
          } else if (editEvent.recurrenceType === "monthly") {
            const monthDiff = editDate.diff(eventDate, 'months');
            newDate = moment(eventData.date).add(monthDiff, 'months').format('YYYY-MM-DD');
          } else if (editEvent.recurrenceType === "yearly") {
            const yearDiff = editDate.diff(eventDate, 'years');
            newDate = moment(eventData.date).add(yearDiff, 'years').format('YYYY-MM-DD');
          }
          await updateDoc(doc(db, "events", eventDoc.id), {
            title: editEvent.title,
            date: newDate,
            time: editEvent.time || null,
            updatedAt: new Date(),
          });
        }
      } else {
        // Update only this event
        const eventRef = doc(db, "events", editEvent.id);
        await updateDoc(eventRef, {
          title: editEvent.title,
          date: editEvent.date,
          time: editEvent.time || null,
          updatedAt: new Date(),
        });
      }

      console.log("Event updated successfully");
      setEditModalOpen(false);
      setEditEvent(null);
      setEditScope("this");

      const snapshot = await getDocs(collection(db, "events"));
      console.log("Fetched events after edit:", snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      const eventList = snapshot.docs.map(doc => {
        const data = doc.data();
        let start, end, dateStr, timeStr;

        if (data.start && typeof data.start.toDate === 'function') {
          const startDate = data.start.toDate();
          const m = moment(startDate);
          dateStr = m.format('YYYY-MM-DD');
          timeStr = (m.hours() === 0 && m.minutes() === 0) ? '' : m.format('HH:mm');
          start = startDate;
          end = data.end && typeof data.end.toDate === 'function' ? data.end.toDate() : startDate;
        } else if (data.time && moment(`${data.date}T${data.time}:00`, "YYYY-MM-DDTHH:mm:ss", true).isValid()) {
          const dateTimeString = `${data.date}T${data.time}:00`;
          start = moment(dateTimeString).toDate();
          end = moment(dateTimeString).toDate();
          dateStr = data.date;
          timeStr = data.time;
        } else if (moment(data.date, "YYYY-MM-DD", true).isValid()) {
          start = moment(data.date).startOf('day').toDate();
          end = moment(data.date).endOf('day').toDate();
          dateStr = data.date;
          timeStr = data.time || '';
        } else {
          return null;
        }

        return {
          id: doc.id,
          title: data.title,
          date: dateStr,
          time: timeStr || "",
          recurrenceId: data.recurrenceId || null,
          recurrenceType: data.recurrenceType || "none",
          start: start,
          end: end,
          allDay: !timeStr,
          location: data.location || "",
          description: data.description || "",
          url: data.url || "",
          category: data.category || "",
          source: data.source || ""
        };
      }).filter(event => event !== null);
      setEvents(eventList);
    } catch (error) {
      console.error("Error updating event:", error);
      setError("Failed to update event: " + error.message);
    }
  };

  const deleteEvent = async (id, recurrenceId, recurrenceType) => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log("Forcing token refresh before delete...");
        const token = await currentUser.getIdToken(true);
        const idTokenResult = await currentUser.getIdTokenResult();
        console.log("Refreshed token:", token);
        console.log("Token claims during delete:", idTokenResult.claims);
        console.log("isAdmin from props:", isAdmin);
      } else {
        throw new Error("No authenticated user");
      }

      if (recurrenceId && editScope === "all") {
        // Delete all events in the series
        const q = query(collection(db, "events"), where("recurrenceId", "==", recurrenceId));
        const snapshot = await getDocs(q);
        for (const eventDoc of snapshot.docs) {
          await deleteDoc(doc(db, "events", eventDoc.id));
        }
        setEvents(events.filter(event => event.recurrenceId !== recurrenceId));
      } else {
        // Delete only this event
        await deleteDoc(doc(db, "events", id));
        setEvents(events.filter(event => event.id !== id));
      }

      console.log("Event(s) deleted successfully");
      setEditModalOpen(false);
      setEditEvent(null);
      setEditScope("this");
    } catch (error) {
      console.error("Error deleting event:", error);
      setError("Failed to delete event: " + error.message);
    }
  };

  const openEditModal = (event) => {
    setEditEvent({ ...event });
    setEditModalOpen(true);
    setEditScope("this"); // Default to editing only this event
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditEvent(null);
    setEditScope("this");
    setError(null);
  };

  // New functions for local events search
  const openSearchModal = () => {
    setSearchModalOpen(true);
    setSearchedEvents([]);
    setSelectedEvents([]);
    setSearchError(null);
  };

  const closeSearchModal = () => {
    setSearchModalOpen(false);
  };

  const handleSearchEvents = async () => {
    setSearchLoading(true);
    setSearchError(null);
    setSearchedEvents([]);
    try {
      const searchLocalEvents = httpsCallable(functions, 'searchLocalEvents');
      const result = await searchLocalEvents({ timeframe });
      const eventsFromGrok = result.data.events || [];
      
      // Filter to future events only
      const futureEvents = eventsFromGrok.filter(ev => moment(ev.date).isAfter(moment()));
      
      setSearchedEvents(futureEvents);
      if (futureEvents.length === 0) {
        setSearchError("No upcoming events found in the selected timeframe.");
      }
    } catch (err) {
      console.error("Error searching events:", err);
      setSearchError("Failed to search for events: " + err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectEvent = (eventIndex) => {
    const selected = [...selectedEvents];
    const index = selected.indexOf(eventIndex);
    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(eventIndex);
    }
    setSelectedEvents(selected);
  };

  const handleAddSelectedToCalendar = async () => {
    for (const index of selectedEvents) {
      const ev = searchedEvents[index];
      let parsedDate = moment(ev.date);
      if (!parsedDate.isValid()) {
        // Incomplete date: Open detail modal to prompt
        setCurrentIncompleteEvent(ev);
        setDetailTitle(ev.name || "");
        setDetailDate(ev.date || moment().format('YYYY-MM-DD'));
        setDetailTime(ev.time || "");
        setDetailModalOpen(true);
        return; // Handle one at a time
      } else {
        const formattedDate = parsedDate.format('YYYY-MM-DD');
        let normalizedTime = ev.time;
        if (ev.time === "All Day" || !ev.time) {
          normalizedTime = ""; // Treat as all day
        }
        const success = await addEventDirect(ev.name, formattedDate, normalizedTime, "none", "1");
        if (!success) {
          console.error('Failed to add event:', ev.name);
        }
      }
    }
    closeSearchModal();
  };

  const handleSaveDetails = async () => {
    if (!detailDate || !moment(detailDate, "YYYY-MM-DD", true).isValid()) {
      setError("Please provide valid date in YYYY-MM-DD format.");
      return;
    }
    let normalizedTime = detailTime;
    if (detailTime === "All Day" || !detailTime) {
      normalizedTime = ""; // Treat as all day
    }
    const success = await addEventDirect(detailTitle, detailDate, normalizedTime, "none", "1");
    if (success) {
      setDetailModalOpen(false);
      setCurrentIncompleteEvent(null);
      handleAddSelectedToCalendar(); // Recurse to handle next
    }
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setCurrentIncompleteEvent(null);
    setError(null);
  };

  // Handle calendar navigation and view changes
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  return (
    <div className="page-container">
      <style>
        {`
          .page-container {
            padding: 2rem;
          }
          .page-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }
          .admin-status {
            margin-bottom: 1rem;
          }
          .error-message {
            color: #ef4444;
            margin-bottom: 1rem;
          }
          .permission-message {
            color: #ef4444;
            margin-bottom: 1rem;
          }
          .add-form {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-bottom: 1rem;
          }
          .form-input {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            flex: 1;
            min-width: 150px;
            font-size: 1rem;
          }
          .form-select {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            flex: 1;
            min-width: 150px;
            font-size: 1rem;
          }
          .checkbox-group {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          .checkbox-label {
            font-size: 1rem;
            color: #374151;
          }
          .add-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .add-button:hover {
            background: #2563eb;
          }
          .calendar-container {
            margin-bottom: 1rem;
            height: 500px;
            opacity: 1; /* Ensure full opacity */
          }
          /* Override react-big-calendar styles */
          .rbc-calendar {
            background: white; /* Solid white background for the calendar */
            opacity: 1 !important; /* Force full opacity */
            border-radius: 0.5rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .rbc-day-bg {
            background: white !important; /* Ensure days without events are opaque */
            opacity: 1 !important;
          }
          .rbc-day-bg.rbc-off-range-bg {
            background: #f3f4f6 !important; /* Slightly different color for off-range days */
            opacity: 1 !important;
          }
          .rbc-event {
            background: #3b82f6 !important; /* Ensure events are visible */
            opacity: 1 !important;
          }
          .event-list-section {
            margin-top: 1rem;
          }
          .event-list-title {
            font-size: 1.25rem;
            margin-bottom: 0.5rem;
          }
          .event-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          .event-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem;
            background: #f3f4f6;
            border-radius: 0.5rem;
            opacity: 1; /* Ensure full opacity */
            cursor: pointer;
          }
          .event-details {
            font-size: 1rem;
          }
          .recurring-label {
            font-size: 0.875rem;
            color: #6b7280;
            margin-left: 0.5rem;
          }
          .event-actions {
            display: flex;
            gap: 0.5rem;
          }
          .edit-button {
            background: #eab308;
            color: white;
            padding: 0.25rem 0.75rem;
            border-radius: 0.25rem;
          }
          .edit-button:hover {
            background: #ca8a04;
          }
          .delete-button {
            background: #ef4444;
            color: white;
            padding: 0.25rem 0.75rem;
            border-radius: 0.25rem;
          }
          .delete-button:hover {
            background: #dc2626;
          }
          .no-events {
            color: #4b5563;
          }
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            padding: 1.5rem;
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            max-width: 500px;
            width: 90%;
            z-index: 1001;
          }
          .modal-title {
            font-size: 1.25rem;
            margin-bottom: 1rem;
          }
          .modal-form {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          .form-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          .form-label {
            color: #374151;
          }
          .form-input-modal {
            border: 1px solid #d1d5e0;
            padding: 0.5rem;
            border-radius: 0.25rem;
            width: 100%;
            font-size: 1rem;
          }
          .scope-group {
            display: flex;
            gap: 1rem;
            align-items: center;
          }
          .scope-label {
            font-size: 1rem;
            color: #374151;
          }
          .modal-buttons {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
          }
          .cancel-button {
            background: #6b7280;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .cancel-button:hover {
            background: #4b5563;
          }
          .save-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .save-button:hover {
            background: #2563eb;
          }
          /* New styles for search modal */
          .search-modal-content {
            max-width: 600px;
            max-height: 80vh;
            overflow-y: auto;
          }
          .search-form {
            display: flex;
            gap: 1rem;
            margin-bottom: 1rem;
          }
          .search-select {
            flex: 1;
          }
          .search-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
          }
          .search-loading {
            text-align: center;
            margin: 1rem 0;
          }
          .event-search-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          .event-search-item {
            display: flex;
            align-items: center;
            padding: 0.75rem;
            background: #f3f4f6;
            border-radius: 0.25rem;
          }
          .event-search-checkbox {
            margin-right: 1rem;
          }
          .event-search-details {
            flex: 1;
          }
          .event-search-name {
            font-weight: bold;
          }
          .add-selected-button {
            background: #22c55e;
            color: white;
            padding: 0.5rem 1rem;
            margin-top: 1rem;
          }
          .add-selected-button:hover {
            background: #16a34a;
          }
          /* Styles for detail modal */
          .detail-modal-content {
            max-width: 400px;
          }
          @media (max-width: 640px) {
            .page-container {
              padding: 1rem;
            }
            .page-title {
              font-size: 1.25rem;
            }
            .admin-status {
              font-size: 0.875rem;
            }
            .error-message,
            .permission-message {
              font-size: 0.875rem;
            }
            .add-form {
              flex-direction: column;
              gap: 0.5rem;
            }
            .form-input,
            .form-select {
              min-width: 100%;
              font-size: 0.9rem;
              padding: 0.4rem;
            }
            .checkbox-group {
              flex-direction: row;
              align-items: center;
            }
            .checkbox-label {
              font-size: 0.875rem;
            }
            .add-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
            .calendar-container {
              height: 400px;
            }
            .event-list-title {
              font-size: 1rem;
            }
            .event-item {
              flex-direction: column;
              align-items: flex-start;
              gap: 0.5rem;
              padding: 0.5rem;
            }
            .event-details {
              font-size: 0.875rem;
            }
            .recurring-label {
              font-size: 0.75rem;
            }
            .event-actions {
              flex-direction: column;
              width: 100%;
            }
            .edit-button,
            .delete-button {
              padding: 0.25rem 0.5rem;
              font-size: 0.875rem;
              width: 100%;
              text-align: center;
            }
            .no-events {
              font-size: 0.875rem;
            }
            .modal-content {
              width: 95%;
              padding: 1rem;
              max-width: 350px;
            }
            .modal-title {
              font-size: 1rem;
            }
            .form-input-modal {
              font-size: 0.9rem;
              padding: 0.4rem;
            }
            .form-label,
            .scope-label {
              font-size: 0.875rem;
            }
            .scope-group {
              flex-direction: column;
              gap: 0.5rem;
            }
            .cancel-button,
            .save-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
            /* Mobile adjustments for search modal */
            .search-modal-content {
              max-width: 95%;
              padding: 1rem;
            }
            .search-form {
              flex-direction: column;
            }
            .search-button {
              width: 100%;
            }
            .event-search-item {
              flex-direction: column;
              align-items: flex-start;
            }
            .add-selected-button {
              width: 100%;
            }
            .detail-modal-content {
              max-width: 95%;
              padding: 1rem;
            }
          }
        `}
      </style>
      <h2 className="page-title">Calendar</h2>
      {error && <p className="error-message">{error}</p>}
      {isAdmin && (
        <>
          <form onSubmit={addEvent} className="add-form">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event Title"
              className="form-input"
              required
            />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="form-input"
              required
            />
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="form-input"
            />
            <select
              value={recurrenceType}
              onChange={(e) => setRecurrenceType(e.target.value)}
              className="form-select"
            >
              <option value="none">No Recurrence</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
            {recurrenceType !== "none" && (
              <input
                type="number"
                value={occurrences}
                onChange={(e) => setOccurrences(e.target.value)}
                placeholder="Number of Occurrences"
                className="form-input"
                min="1"
                required
              />
            )}
            <button type="submit" className="add-button">
              Add Event
            </button>
          </form>
          <button onClick={openSearchModal} className="add-button" style={{ marginTop: '1rem' }}>
            Find Local Events
          </button>
        </>
      )}
      <div className="calendar-container">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%" }}
          view={view}
          onView={handleViewChange}
          date={currentDate}
          onNavigate={handleNavigate}
          onSelectEvent={(event) => setSelectedEvent(event)}
        />
      </div>
      <div className="event-list-section">
        <h3 className="event-list-title">Event List</h3>
        {displayedEvents.length > 0 ? (
          <div className="event-list">
            {displayedEvents.map(event => (
              <div key={event.id} className="event-item" onClick={() => setSelectedEvent(event)}>
                <span className="event-details">
                  {event.title} — {event.date} {event.time ? `at ${event.time}` : "(All Day)"}
                  {event.location && <span style={{ color: '#4b5563', fontSize: '0.9em' }}> · {event.location}</span>}
                  {event.recurrenceId && (
                    <span className="recurring-label">
                      (Recurring {event.recurrenceType.charAt(0).toUpperCase() + event.recurrenceType.slice(1)})
                    </span>
                  )}
                </span>
                {isAdmin && (
                  <div className="event-actions">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
                      className="edit-button"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteEvent(event.id, event.recurrenceId, event.recurrenceType); }}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="no-events">No events found</p>
        )}
      </div>

      {editModalOpen && (
        <div className="modal-overlay">
          <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
            <h3 className="modal-title">Edit Event</h3>
            {error && <p className="error-message">{error}</p>}
            <form onSubmit={editEventData} className="modal-form">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={editEvent?.title || ""}
                  onChange={(e) => setEditEvent({ ...editEvent, title: e.target.value })}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  value={editEvent?.date || ""}
                  onChange={(e) => setEditEvent({ ...editEvent, date: e.target.value })}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Time (optional, leave blank for All Day)</label>
                <input
                  type="time"
                  value={editEvent?.time || ""}
                  onChange={(e) => setEditEvent({ ...editEvent, time: e.target.value })}
                  className="form-input-modal"
                />
              </div>
              {editEvent?.recurrenceId && (
                <div className="form-group">
                  <label className="form-label">Apply changes to:</label>
                  <div className="scope-group">
                    <label className="scope-label">
                      <input
                        type="radio"
                        value="this"
                        checked={editScope === "this"}
                        onChange={(e) => setEditScope(e.target.value)}
                      />
                      This event only
                    </label>
                    <label className="scope-label">
                      <input
                        type="radio"
                        value="all"
                        checked={editScope === "all"}
                        onChange={(e) => setEditScope(e.target.value)}
                      />
                      All events in the series
                    </label>
                  </div>
                </div>
              )}
              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {searchModalOpen && (
        <div className="modal-overlay">
          <div className={`modal-content search-modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
            <h3 className="modal-title">Find Local Events</h3>
            {searchError && <p className="error-message">{searchError}</p>}
            <div className="search-form">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="form-select search-select"
              >
                <option value="1 month">1 Month</option>
                <option value="2 months">2 Months</option>
                <option value="3 months">3 Months</option>
                <option value="6 months">6 Months</option>
                <option value="1 year">1 Year</option>
              </select>
              <button onClick={handleSearchEvents} className="search-button" disabled={searchLoading}>
                Search
              </button>
            </div>
            {searchLoading && <p className="search-loading">Searching for events...</p>}
            {searchedEvents.length > 0 && (
              <>
                <div className="event-search-list">
                  {searchedEvents.map((ev, index) => (
                    <div key={index} className="event-search-item">
                      <input
                        type="checkbox"
                        className="event-search-checkbox"
                        checked={selectedEvents.includes(index)}
                        onChange={() => handleSelectEvent(index)}
                      />
                      <div className="event-search-details">
                        <div className="event-search-name">{ev.name}</div>
                        <div>Date: {ev.date || "Missing"}</div>
                        <div>Time: {ev.time || "Missing"}</div>
                        <div>Location: {ev.location}</div>
                        <div>{ev.description}</div>
                        {ev.url && <a href={ev.url} target="_blank" rel="noopener noreferrer">More Info</a>}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleAddSelectedToCalendar}
                  className="add-selected-button"
                  disabled={selectedEvents.length === 0}
                >
                  Add Selected to Calendar
                </button>
              </>
            )}
            <div className="modal-buttons">
              <button type="button" onClick={closeSearchModal} className="cancel-button">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {detailModalOpen && (
        <div className="modal-overlay">
          <div className={`modal-content detail-modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
            <h3 className="modal-title">Complete Event Details</h3>
            {error && <p className="error-message">{error}</p>}
            <form onSubmit={(e) => { e.preventDefault(); handleSaveDetails(); }} className="modal-form">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={detailTitle}
                  onChange={(e) => setDetailTitle(e.target.value)}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Date (YYYY-MM-DD)</label>
                <input
                  type="date"
                  value={detailDate}
                  onChange={(e) => setDetailDate(e.target.value)}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Time (HH:MM or leave blank for All Day)</label>
                <input
                  type="time"
                  value={detailTime}
                  onChange={(e) => setDetailTime(e.target.value)}
                  className="form-input-modal"
                />
              </div>
              <div className="modal-buttons">
                <button type="button" onClick={closeDetailModal} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Save and Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedEvent && (
        <div className="modal-overlay">
          <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
            <h2 className="modal-title">{selectedEvent.title}</h2>
            <p><strong>Date:</strong> {selectedEvent.date} {selectedEvent.time ? `at ${selectedEvent.time}` : '(All Day)'}</p>
            {selectedEvent.location && <p><strong>Location:</strong> {selectedEvent.location}</p>}
            {selectedEvent.description && <p style={{ whiteSpace: 'pre-wrap' }}><strong>About:</strong> {selectedEvent.description}</p>}
            {selectedEvent.source && <p><strong>Source:</strong> {selectedEvent.source}</p>}
            {selectedEvent.recurrenceId && (
              <p><strong>Recurrence:</strong> {selectedEvent.recurrenceType.charAt(0).toUpperCase() + selectedEvent.recurrenceType.slice(1)}</p>
            )}
            {selectedEvent.url && (
              <p><a href={selectedEvent.url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>More details & tickets →</a></p>
            )}
            <div className="modal-buttons">
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                className="cancel-button"
              >
                Close
              </button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => openEditModal(selectedEvent)}
                    className="edit-button"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteEvent(selectedEvent.id, selectedEvent.recurrenceId, selectedEvent.recurrenceType)}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;